/* eslint-disable react-hooks/rules-of-hooks */
// src/components/forms/QuestionInput.jsx
import React, { useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { motion } from "framer-motion";

const QuestionInput = ({ question, value, onChange, disabled }) => {
  // === Hooks always at the top ===
  const [sigPad, setSigPad] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState("");

  // === Strong protection from undefined/null ===
  if (!question || typeof question !== "object" || !question.id) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-300 rounded-lg text-center">
        <p className="text-yellow-800 text-sm">Loading question...</p>
      </div>
    );
  }

  // DEBUG: Check what the question object looks like
  if (question.type === 'radio' || question.type === 'checkbox_group') {
     console.log('Rendering Question:', question.label);
     console.log('Type:', question.type);
     console.log('Options:', JSON.stringify(question.options, null, 2));
     console.log('Option Labels:', question.option_labels);
  }

  const HelpTooltip = ({ text }) => {
    const [open, setOpen] = useState(false);

    if (!text) return null;

    return (
      <div className="relative inline-block">
        {/* Help Button */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-2 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 shadow-md transition-all duration-200"
          aria-label="Help"
          title="Click for help"
        >
          ?
        </button>

        {/* SweetAlert-style Popup */}
        {open && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-30 z-40"
              onClick={() => setOpen(false)}
            />

            {/* Popup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-in fade-in zoom-in-95">
                {/* Icon Circle */}
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center shadow-lg">
                  <span className="text-white text-4xl font-bold">?</span>
                </div>

                {/* Content */}
                <div className="mt-12 text-center">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Need Help?
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-base px-4">
                    {text}
                  </p>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setOpen(false)}
                  className="mt-8 w-full py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-200 shadow-md"
                >
                  Got it!
                </button>
              </div>
            </motion.div>
          </>
        )}
      </div>
    );
  };

  // === Safe data extraction ===
  const {
    type = "text",
    options = null,
    max_length,
    required = false,
    allow_custom = false,
    id,
    label,
    help_text,
  } = question;

  // === Safely convert options to array ===
  const safeOptions = (() => {
    if (Array.isArray(options)) {
      return options.map((opt) => {
        // Handle explicit object format {value, label}
        if (opt && typeof opt === "object" && "value" in opt) {
          return opt;
        }
        // Handle simple string/number options, using option_labels if available
        const val = String(opt);
        let lab =
          question.option_labels && question.option_labels[val]
            ? question.option_labels[val]
            : val;
            
        // Fallback for common Yes/No if no label found
        if (lab === "Y") lab = "Yes";
        if (lab === "N") lab = "No";

        return { value: val, label: lab };
      });
    }

    // Handle legacy object format { value: label }
    if (options && typeof options === "object") {
      return Object.entries(options).map(([value, label]) => ({
        value,
        label,
      }));
    }

    return [];
  })();

  // === Functions ===
  const handleChange = (newValue) => {
    if (disabled) return;
    onChange(newValue);
  };

  const clearSignature = () => {
    sigPad?.clear();
    handleChange("");
  };

  useEffect(() => {
    if (type === "select" && allow_custom && value === "other") {
      setShowCustomInput(true);
    } else if (type === "select" && value !== "other") {
      setShowCustomInput(false);
      setCustomValue("");
    }
  }, [value, type, allow_custom]);

  switch (type) {
    case "text":
    case "email":
    case "phone":
    case "number":
      return (
        <div className="space-y-1">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>
          <input
            type={
              type === "phone"
                ? "tel"
                : type === "number"
                  ? "number"
                  : type === "email"
                    ? "email"
                    : "text"
            }
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            maxLength={max_length || undefined}
            disabled={disabled}
            required={required}
            placeholder={
              type === "phone"
                ? "+962 7xx xxx xxx"
                : type === "email"
                  ? "name@example.com"
                  : type === "number"
                    ? "123"
                    : "Enter your answer..."
            }
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition"
          />
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-1">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>
          <textarea
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            rows={5}
            maxLength={max_length || undefined}
            disabled={disabled}
            required={required}
            placeholder="Enter your detailed answer..."
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed resize-none transition"
          />
        </div>
      );

    case "date":
      return (
        <div className="space-y-1">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>

          <input
            type="date"
            value={value ? new Date(value).toISOString().split("T")[0] : ""}
            onChange={(e) => {
              const isoDate = e.target.value; // "2025-11-10"
              if (!isoDate) {
                handleChange("");
                return;
              }

              // Convert from YYYY-MM-DD to MM/DD/YYYY
              const [year, month, day] = isoDate.split("-");
              const formattedDate = `${month}/${day}/${year}`; // "11/10/2025"

              handleChange(formattedDate);
            }}
            disabled={disabled}
            required={required}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition"
          />
        </div>
      );

    case "select":
      const hasOptions = safeOptions.length > 0;
      const shouldAllowCustom = allow_custom === true;

      return (
        <div className="space-y-2">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>
          <select
            value={showCustomInput ? "other" : value || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "other") {
                setShowCustomInput(true);
                handleChange("other");
              } else {
                setShowCustomInput(false);
                setCustomValue("");
                handleChange(val);
              }
            }}
            disabled={disabled}
            required={required && !value}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition"
          >
            <option value="" disabled>
              {hasOptions ? "Choose an option..." : "No options available"}
            </option>
            {hasOptions &&
              safeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            {shouldAllowCustom && (
              <option value="other">Other (type here)</option>
            )}
          </select>

          {showCustomInput && (
            <input
              type="text"
              value={customValue}
              onChange={(e) => {
                setCustomValue(e.target.value);
                handleChange(e.target.value);
              }}
              placeholder="Type your answer here..."
              disabled={disabled}
              required={required}
              className="w-full px-4 py-3 text-base border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 transition"
            />
          )}
        </div>
      );

    case "radio":
      return (
        <div className="space-y-3">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>
          {safeOptions.length > 0 ? (
            safeOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
              >
                <input
                  type="radio"
                  name={`radio-${id}`}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleChange(e.target.value)}
                  disabled={disabled}
                  required={required && !value}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700 font-medium">{opt.label}</span>
              </label>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic">No options available</p>
          )}
        </div>
      );

    case "checkbox_group":
      const currentValues = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-3">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>
          {safeOptions.length > 0 ? (
            safeOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition"
              >
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={currentValues.includes(opt.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleChange([...currentValues, opt.value]);
                    } else {
                      handleChange(
                        currentValues.filter((v) => v !== opt.value)
                      );
                    }
                  }}
                  disabled={disabled}
                  className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 font-medium">{opt.label}</span>
              </label>
            ))
          ) : (
            <p className="text-sm text-gray-500 italic">No options available</p>
          )}
        </div>
      );

    case "checkbox":
      // إذا في validation: ["boolean"] → نرسل true/false
      const isBoolean = Array.isArray(question.validation) && question.validation.includes("boolean");
      const currentBoolValue = isBoolean
        ? (value === true || value === "true")
        : (value === "yes" || value === true);

      return (
        <label className="flex items-center space-x-3 cursor-pointer p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
          <input
            type="checkbox"
            checked={currentBoolValue}
            onChange={(e) => {
              const newVal = isBoolean ? e.target.checked : (e.target.checked ? "yes" : "");
              handleChange(newVal);
            }}
            disabled={disabled}
            required={required && !currentBoolValue}
            className="h-6 w-6 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700 font-medium">
            {label || (currentBoolValue ? "Yes" : "No")}
          </span>
        </label>
      );

    case "tel":
      return (
        <div className="space-y-1">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && <HelpTooltip text={help_text} />}
          </div>

          <input
            type="tel"
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            required={required}
            placeholder="+1 (123) xxx xxx" 
            inputMode="tel"
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition"
          />
        </div>
      );

     case "signature":
      return (
        <div className="space-y-3">
          <div className="flex justify-between mb-[15px]">
            {label && (
              <label className="block text-sm font-medium text-gray-700">
                {required && <span className="text-red-500 mr-1">*</span>}
                {label}
              </label>
            )}
            {help_text && (
              <HelpTooltip text={help_text} />
            )}
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-gray-50">
            <SignatureCanvas
              ref={setSigPad}
              penColor="black"
              canvasProps={{
                className: "w-full h-48 bg-white rounded-md",
                style: { touchAction: "none" },
              }}
              onEnd={() => {
                const dataUrl = sigPad
                  ?.getTrimmedCanvas()
                  .toDataURL("image/png");
                handleChange(dataUrl || "");
              }}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={clearSignature}
              disabled={disabled}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Clear Signature
            </button>
            {value && (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
              >
                View Signature
              </a>
            )}
          </div>
        </div>
      );

    default:
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">
            <strong>Unsupported type:</strong> {type}
          </p>
          <p className="text-xs text-red-600 mt-1">ID: {id}</p>
        </div>
      );
  }
};

// Wrapper Component to handle Recursion
const QuestionInputWrapper = (props) => {
    const { 
        question, 
        value, 
        dependentQuestions, 
        dependentAnswers, 
        onDependentChange, 
        isConditionMet 
    } = props;

    // Render Main Question
    const mainContent = <QuestionInput {...props} />;
    
    // Render Visible Dependents
    const visibleDependents = (dependentQuestions || []).filter(dq => {
         return isConditionMet ? isConditionMet(dq, value) : false;
    });

    if (visibleDependents.length === 0) {
        return mainContent;
    }

    return (
       <div className="space-y-4">
           {/* Main Question */}
           {mainContent}

           {/* Dependent Questions (Indented/Styled) */}
           <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="pl-6 border-l-2 border-blue-100 space-y-4"
           >
               {visibleDependents.map(dq => (
                   <div key={dq.id} className="bg-blue-50/30 p-4 rounded-lg">
                       <QuestionInputWrapper 
                           question={dq}
                           value={dependentAnswers[dq.id]}
                           onChange={(val) => onDependentChange(dq.id, val)}
                           disabled={props.disabled}
                           // Pass down the chain (though nested dependents are rare)
                           dependentQuestions={dq.dependent_questions} 
                           dependentAnswers={dependentAnswers} // Use same flattened state
                           onDependentChange={onDependentChange}
                           isConditionMet={(subQ, subVal) => isConditionMet(subQ, dependentAnswers[dq.id])} // Recursion?
                           // Actually, logic usually depends on PARENT value.
                           // But isConditionMet helper logic handles checking Source ID.
                           // Our helper in parent is: (depQ, mainValue) -> checks 'depQ' logic against 'mainValue'.
                           // If nested, we need to correct context.
                           // For now, assume 1-level depth (Other -> Explanation).
                           // We just pass isConditionMet but bound to CURRENT value?
                           // Actually, isConditionMet assumes 'mainValue' IS the source value.
                           // So for sub-dependent, 'mainValue' should be 'value' (of this wrapper).
                           // Yes.
                       />
                   </div>
               ))}
           </motion.div>
       </div>
    );
};

export default QuestionInputWrapper;